import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from '../lib/errorReporter';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Catches render-time crashes anywhere below it, reports them into
 *  Operational Logs (see lib/errorReporter.ts), and shows a recoverable
 *  fallback instead of an unmounted white screen. Uncaught exceptions and
 *  unhandled promise rejections OUTSIDE React's render cycle are reported
 *  separately via installGlobalErrorReporting() in main.tsx -- React never
 *  routes those through this boundary. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError('react-boundary', error.message, {
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-[#1e3c72] to-[#2a5298] flex items-center justify-center p-6">
          <div className="max-w-md rounded-2xl border border-white/20 bg-slate-950/80 p-6 text-center text-white shadow-2xl">
            <div className="text-lg font-bold">Something went wrong</div>
            <p className="mt-2 text-sm text-white/70">
              This error has been logged and is visible to admins under Operational Logs.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded-xl bg-blue-500 px-4 py-2 text-sm font-bold hover:bg-blue-400"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
